import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { PrintAction } from "src/actions/datatable"

import FormIconTextField from "src/components/Form/FormIconTextField"
import Pagination from "src/components/Pagination"
import ScrollShadow from "src/components/ScrollShadow"
import Skeleton from "src/components/Skeleton"

import useFuseSearch from "src/hooks/useFuseSearch"

import ActionConfirmModal from "./ActionConfirmModal"
import CardView from "./CardView"
import PrintTable from "./PrintTable"
import RowActions from "./RowActions"
import TableToolbar from "./TableToolbar"
import {
  ActionPropType,
  ColumnPropType,
  RowConfigPropType,
  SearchConfigPropType,
  SelectionPropType,
  SortConfigPropType,
} from "./types"
import { useColumnVisibility } from "./useColumnVisibility"
import { useResponsiveView } from "./useResponsiveView"
import { useSelection } from "./useSelection"

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, [role="button"]'

const SKELETON_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-5/6", "w-3/5"]

const TABLE_ROW_VARIANTS = {
  default: "",
  parent: "group",
}

const TEXT_COLORS = {
  default: "",
  gray: "text-gray-600",
}

const ROW_COLORS = {
  default: "bg-white",
  shaded: "bg-gray-100",
}

const ROW_HOVER_COLORS = {
  default: "bg-gray-100",
  shaded: "bg-gray-200",
}

const STICKY_COLUMN_WIDTH = 50
const ACTIONS_BUTTON_SIZE = 32
const ACTIONS_GAP = 12
const ACTIONS_HORIZONTAL_PADDING = 32
const MAX_VISIBLE_ROW_ACTIONS = 5

const DataTableV2 = ({
  name,
  columns,
  data,
  idKey = "id",
  actions = [],
  rowConfig,
  selection: selectionProp,
  onSelectionChange,
  totalCount,
  allSelectableIds,
  pagination = false,
  page,
  numberOfPages,
  onPageChange,
  sortConfig,
  isLoading = false,
  maxHeight = "calc(100vh - 250px)",
  hideSettings = false,
  hideActions = false,
  infinite = false,
  rowCount = 5,
  isRowSelectable,
  emptyState,
  onPrint,
  renderPrintSubRow,
  onScroll,
  searchConfig,
  footerRow,
}) => {
  const [hoveredRowId, setHoveredRowId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [isHoveringInteractive, setIsHoveringInteractive] = useState(false)
  const [isHoveringActions, setIsHoveringActions] = useState(false)
  const [menuOpenRowId, setMenuOpenRowId] = useState(null)
  const [focusedRowId, setFocusedRowId] = useState(null)
  const [printRows, setPrintRows] = useState(null)
  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [pendingRow, setPendingRow] = useState(null)
  const [pendingSelection, setPendingSelection] = useState(null)

  const handleRequestConfirm = useCallback(
    (action, row = null, selected = null) => {
      setPendingAction(action)
      setPendingRow(row)
      setPendingSelection(selected)
    },
    []
  )

  const handleCloseConfirmModal = useCallback(() => {
    setPendingAction(null)
    setPendingRow(null)
    setPendingSelection(null)
  }, [])

  const handleConfirmAction = useCallback(() => {
    if (pendingAction?.onClickSingle && pendingRow) {
      pendingAction.onClickSingle(pendingRow)
    } else if (pendingAction?.onClickBulk && Array.isArray(pendingSelection)) {
      pendingAction.onClickBulk(pendingSelection)
    }
    handleCloseConfirmModal()
  }, [pendingAction, pendingRow, pendingSelection, handleCloseConfirmModal])

  const confirmMessage = useMemo(() => {
    const message = pendingAction?.confirm?.message
    if (typeof message === "function") {
      return message(pendingRow ?? pendingSelection)
    }
    return message ?? ""
  }, [pendingAction, pendingRow, pendingSelection])

  // Clear hover state when an action is performed (for overflow menu items
  // which render inside the row and don't trigger onMouseLeave)
  const clearHover = useCallback(() => {
    setHoveredRowId(null)
    setIsHoveringInteractive(false)
    setIsHoveringActions(false)
    setMenuOpenRowId(null)
  }, [])

  const printAction = useMemo(() => {
    if (!onPrint) return null
    return PrintAction({
      onPrint: async (selectedIds) => {
        setIsPrintLoading(true)
        try {
          const data = await onPrint(selectedIds)
          setPrintRows(data)
        } finally {
          setIsPrintLoading(false)
        }
      },
      disabled: isPrintLoading,
    })
  }, [onPrint, isPrintLoading])

  useEffect(() => {
    if (printRows) {
      requestAnimationFrame(() => {
        window.print()
        setPrintRows(null)
      })
    }
  }, [printRows])

  const hasViewUrl = !!rowConfig?.getViewUrl

  // When rowConfig.getViewUrl is provided, auto-generate a view action
  // so the eye icon appears in the RowActions toolbar alongside other actions.
  const effectiveActions = useMemo(() => {
    let merged = actions
    if (hasViewUrl) {
      const hasExistingViewAction = actions.some((a) => a.role === "view")
      if (!hasExistingViewAction) {
        merged = [
          {
            label: "View",
            icon: "eye",
            role: "view",
            href: rowConfig.getViewUrl,
            ...(rowConfig.onViewClick && {
              onClickSingle: (row) => rowConfig.onViewClick(row, "action"),
            }),
          },
          ...merged,
        ]
      }
    }
    if (printAction) {
      merged = [...merged, printAction]
    }
    return merged
  }, [actions, hasViewUrl, rowConfig, printAction])

  const hasSingleRowActions =
    !hideActions &&
    effectiveActions.some((action) => action.onClickSingle || action.href)

  const hasBulkActions = effectiveActions.some((action) => action.onClickBulk)

  // Left column appears when we have bulk actions OR a view URL indicator
  const hasLeftColumn = hasBulkActions || hasViewUrl

  // Actions column only needed when there are row actions to display
  const hasActionsColumn = hasSingleRowActions

  // Reserve enough sticky-column width so row actions never overlap data cells.
  // Keep this in sync with RowActions button layout.
  const actionsColumnWidth = useMemo(() => {
    if (!hasActionsColumn) return STICKY_COLUMN_WIDTH

    const singleRowActionCount = effectiveActions.filter(
      (action) => action.onClickSingle || action.href
    ).length
    const displayedActions = Math.min(
      singleRowActionCount,
      MAX_VISIBLE_ROW_ACTIONS
    )
    const hasOverflowMenu = singleRowActionCount > MAX_VISIBLE_ROW_ACTIONS
    const totalItems = displayedActions + (hasOverflowMenu ? 1 : 0)

    if (totalItems === 0) return STICKY_COLUMN_WIDTH

    const contentWidth =
      totalItems * ACTIONS_BUTTON_SIZE + (totalItems - 1) * ACTIONS_GAP
    return Math.max(
      STICKY_COLUMN_WIDTH,
      contentWidth + ACTIONS_HORIZONTAL_PADDING
    )
  }, [effectiveActions, hasActionsColumn])

  // Show toolbar if we have bulk actions OR column settings
  const showToolbar = hasBulkActions || !hideSettings

  const rows = useMemo(
    () =>
      data?.map((row, index) => ({
        ...row,
        id: row[idKey] ?? row.id ?? `row-${index}`,
      })),
    [data, idKey]
  )

  const isRowSelectableFn = useMemo(
    () => isRowSelectable ?? (() => true),
    [isRowSelectable]
  )

  const pageSelectableIds = useMemo(
    () =>
      rows?.filter((row) => isRowSelectableFn(row)).map((row) => row.id) ?? [],
    [rows, isRowSelectableFn]
  )

  const {
    selection,
    selectedCount,
    allPageRowsSelected,
    isSelected,
    toggleRow,
    selectAll: handleSelectAll,
    deselectAll: handleDeselectAll,
    selectAllAcrossPages: handleSelectAllAcrossPages,
    effectiveTotalCount,
  } = useSelection({
    selection: selectionProp,
    onSelectionChange,
    totalCount,
    data: rows,
    pageSelectableIds,
    allSelectableIds,
  })

  // Manage column visibility with localStorage persistence
  const [managedColumns, toggleColumnVisibility] = useColumnVisibility({
    columns,
    name,
  })

  // Filter out hidden columns
  const visibleColumns = managedColumns.filter((col) => !col.hide)

  // Compute the effective sort handler.
  // When onSortChange is provided (new API), the component handles the
  // asc → desc → clear toggle internally. When onColumnSort is provided
  // (legacy API), the raw column key is passed through unchanged.
  const effectiveSortHandler = useMemo(() => {
    if (sortConfig?.onColumnSort) return sortConfig.onColumnSort

    if (sortConfig?.onSortChange) {
      return (columnKey) => {
        let key = columnKey
        let direction = "asc"

        if (sortConfig.sortKey === columnKey) {
          const current = sortConfig.sortDirection?.toLowerCase()
          if (current === "asc") {
            direction = "desc"
          } else if (current === "desc") {
            key = null
            direction = null
          }
        }

        sortConfig.onSortChange({ key, direction })
      }
    }

    return undefined
  }, [sortConfig])

  // Build a sortConfig with the effective handler for child components
  const effectiveSortConfig = useMemo(() => {
    if (!sortConfig) return undefined
    return { ...sortConfig, onColumnSort: effectiveSortHandler }
  }, [sortConfig, effectiveSortHandler])

  // Check if we should show mobile card view
  const { shouldShowCards } = useResponsiveView(columns)

  // Handler for action clicks in CardView (respects confirmation)
  const handleCardActionClick = useCallback(
    (action, row) => {
      if (action.confirm) {
        handleRequestConfirm(action, row)
      } else if (action.onClickSingle) {
        action.onClickSingle(row)
      }
    },
    [handleRequestConfirm]
  )

  const pageSelectableCount = pageSelectableIds.length

  // Check if all items across all pages are already selected
  const allItemsSelected = useMemo(() => {
    if (!allSelectableIds || allSelectableIds.length === 0) return false
    const selectionSet = new Set(selection)
    return allSelectableIds.every((id) => selectionSet.has(id))
  }, [allSelectableIds, selection])

  // Show "Select all X items" banner when:
  // - Bulk actions are enabled
  // - Multiple pages exist
  // - All items on current page are selected
  // - Not all items across pages are selected yet
  // - We have allSelectableIds available (required for explicit IDs approach)
  const showSelectAllBanner =
    hasBulkActions &&
    numberOfPages > 1 &&
    allPageRowsSelected &&
    !allItemsSelected &&
    allSelectableIds &&
    allSelectableIds.length > (rows?.length ?? 0)

  const filteredRows = useFuseSearch(
    rows ?? [],
    searchConfig?.keys ?? [],
    searchConfig ? searchTerm : ""
  )

  const isEmpty = !isLoading && (!rows || rows.length === 0)
  const isSearchEmpty = Boolean(
    searchConfig &&
      !isLoading &&
      rows?.length > 0 &&
      filteredRows.length === 0 &&
      searchTerm.trim()
  )

  if (isEmpty) {
    return (
      <div className="datatable-v2-print" data-testid="datatable-v2">
        <div
          className="rounded bg-white py-12 text-center"
          data-testid="empty-state"
        >
          {emptyState || (
            <span className="text-gray-600">No results found</span>
          )}
        </div>
      </div>
    )
  }

  const searchInput = searchConfig ? (
    <div className="mb-4 w-1/2 print:hidden">
      <FormIconTextField
        icon={<i className="icon icon-search-mdi text-xl text-gray-400" />}
        placeholder={searchConfig.placeholder || "Search..."}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        isClearable
        onClearSelection={() => setSearchTerm("")}
      />
    </div>
  ) : null

  if (isSearchEmpty) {
    return (
      <div className="datatable-v2-print" data-testid="datatable-v2">
        {searchInput}
        <div
          className="rounded bg-white py-12 text-center"
          data-testid="empty-state"
        >
          <span className="text-gray-600">No results match your search.</span>
        </div>
      </div>
    )
  }

  // Render mobile card view when appropriate
  if (shouldShowCards) {
    return (
      <div className="datatable-v2-print" data-testid="datatable-v2">
        {searchInput}
        <div className="print:hidden">
          <CardView
            columns={columns}
            data={filteredRows}
            actions={effectiveActions}
            isSelected={hasBulkActions ? isSelected : undefined}
            onToggleSelection={hasBulkActions ? toggleRow : undefined}
            onActionClick={handleCardActionClick}
            hasBulkActions={hasBulkActions}
            onRequestConfirm={handleRequestConfirm}
            selectedCount={selectedCount}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            allPageRowsSelected={allPageRowsSelected}
            showSelectAllBanner={showSelectAllBanner}
            onSelectAllAcrossPages={handleSelectAllAcrossPages}
            totalCount={effectiveTotalCount}
            pageSelectableCount={pageSelectableCount}
            selection={selection}
            isRowSelectable={isRowSelectable}
            sortConfig={effectiveSortConfig}
            isLoading={isLoading}
            rowCount={rowCount}
            emptyState={emptyState}
          />
        </div>
        <ActionConfirmModal
          isOpen={pendingAction !== null}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmAction}
          title={pendingAction?.confirm?.title ?? ""}
          message={confirmMessage}
          confirmLabel={pendingAction?.confirm?.confirmLabel ?? "Confirm"}
          danger={pendingAction?.danger ?? false}
        />
        {pagination && numberOfPages > 1 && (
          <div className="mt-2 print:hidden">
            <Pagination
              numberOfPages={numberOfPages}
              page={page}
              onPageChange={onPageChange}
            />
          </div>
        )}
        {printRows && (
          <PrintTable
            data={printRows}
            columns={managedColumns}
            idKey={idKey}
            renderSubRow={renderPrintSubRow}
          />
        )}
      </div>
    )
  }

  return (
    <div className="datatable-v2-print" data-testid="datatable-v2">
      {searchInput}
      <div
        className={classNames(
          "flex min-h-[104px] flex-col overflow-hidden rounded border",
          { "print:hidden": printRows }
        )}
        style={{ maxHeight }}
      >
        {showToolbar && (
          <div className="sticky top-0 z-[150] flex-shrink-0 print:hidden">
            <TableToolbar
              actions={effectiveActions}
              selection={selection}
              selectedCount={selectedCount}
              totalCount={effectiveTotalCount}
              pageSelectableCount={pageSelectableCount}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              allPageRowsSelected={allPageRowsSelected}
              showSelectAllBanner={showSelectAllBanner}
              onSelectAllAcrossPages={handleSelectAllAcrossPages}
              hasBulkActions={hasBulkActions}
              onRequestConfirm={handleRequestConfirm}
              hideSettings={hideSettings}
              columns={managedColumns}
              onToggleColumn={toggleColumnVisibility}
            />
          </div>
        )}
        {/* Scrollable table area with scroll shadow indicators */}
        <ScrollShadow
          direction="horizontal"
          className="flex min-h-0 flex-1 flex-col"
          scrollContainerClassName="relative z-0 min-h-0 flex-1"
          zIndex={130}
          onScroll={onScroll}
          startShadowStyle={{
            top: 40,
            bottom: 0,
            left: hasLeftColumn ? STICKY_COLUMN_WIDTH : 0,
          }}
        >
          <table
            className={classNames("relative w-[100%] min-w-full table-fixed", {
              "is-infinite": infinite,
            })}
          >
            <thead className="sticky top-0 z-[120] print:static">
              <tr className="h-10 border-b bg-gray-200">
                {hasLeftColumn && (
                  <th
                    className="bg-gray-200 print:hidden"
                    style={{
                      width: STICKY_COLUMN_WIDTH,
                      minWidth: STICKY_COLUMN_WIDTH,
                    }}
                  />
                )}
                {visibleColumns.map((col, colIndex) => {
                  const columnSortKey = col.sortKey || col.key
                  const isSortColumn = sortConfig?.sortKey === columnSortKey
                  const sortDirection = sortConfig?.sortDirection?.toLowerCase()
                  const headerAlign = col.headerAlign || "left"

                  const renderSortIcon = () => {
                    if (sortDirection === "asc" && isSortColumn) {
                      return <i className="icon icon-sort-asc ml-2 text-xs" />
                    } else if (sortDirection === "desc" && isSortColumn) {
                      return <i className="icon icon-sort-desc ml-2 text-xs" />
                    } else {
                      return <i className="icon icon-sort-fa ml-2 text-xs" />
                    }
                  }

                  return (
                    <th
                      key={col.key}
                      className={classNames(
                        "overflow-hidden whitespace-nowrap px-4 font-semibold print:text-xs",
                        {
                          "cursor-pointer hover:text-gray-600": col.sortable,
                          "text-left": headerAlign === "left",
                          "text-center": headerAlign === "center",
                          "text-right": headerAlign === "right",
                          "text-justify": headerAlign === "justify",
                        }
                      )}
                      style={{ width: col.width || "200px" }}
                      role={col.sortable ? "button" : undefined}
                      onClick={
                        col.sortable && effectiveSortHandler
                          ? () => effectiveSortHandler(columnSortKey)
                          : undefined
                      }
                    >
                      {col.sortable ? (
                        <div
                          className={classNames("flex items-center", {
                            "justify-center": headerAlign === "center",
                            "justify-end": headerAlign === "right",
                          })}
                        >
                          {col.header}
                          {renderSortIcon()}
                        </div>
                      ) : (
                        col.header
                      )}
                    </th>
                  )
                })}
                <th className="print:hidden" style={{ width: "auto" }}></th>
                {hasActionsColumn && (
                  <th
                    className="bg-gray-200 print:hidden"
                    style={{
                      width: actionsColumnWidth,
                      minWidth: actionsColumnWidth,
                      maxWidth: actionsColumnWidth,
                    }}
                  />
                )}
              </tr>
            </thead>
            <tbody className="relative">
              {isLoading &&
                Array.from({ length: rowCount }, (_, i) => (
                  <tr
                    key={`skeleton-${i}`}
                    className="h-16 border-b bg-white last:border-none"
                    data-testid="skeleton-row"
                  >
                    {hasLeftColumn && <td />}
                    {visibleColumns.map((col, colIndex) => (
                      <td key={col.key} className="px-4">
                        <Skeleton.Bar
                          width={
                            SKELETON_WIDTHS[
                              (i + colIndex) % SKELETON_WIDTHS.length
                            ]
                          }
                        />
                      </td>
                    ))}
                    <td className="print:hidden" />
                    {hasActionsColumn && <td className="print:hidden" />}
                  </tr>
                ))}
              {!isLoading &&
                filteredRows?.map((row) => {
                  const isRowHovered = hoveredRowId === row.id
                  const isRowFocused =
                    focusedRowId === row.id && hoveredRowId === null
                  const isMenuOpenForRow = menuOpenRowId === row.id
                  const showRowHighlight =
                    (isRowHovered &&
                      !isHoveringInteractive &&
                      !isHoveringActions) ||
                    isRowFocused ||
                    isMenuOpenForRow
                  const showActions =
                    (isRowHovered && !isHoveringInteractive) ||
                    isRowFocused ||
                    isMenuOpenForRow
                  const rowSelectable = isRowSelectableFn(row)

                  const subRowColumn = visibleColumns.find(
                    (col) => typeof col.renderSubRow === "function"
                  )
                  const subRowContent = subRowColumn?.renderSubRow(row)
                  const dataRowCellCount =
                    (hasLeftColumn ? 1 : 0) +
                    visibleColumns.length +
                    1 +
                    (hasActionsColumn ? 1 : 0)

                  // Extract rowConfig values for this row
                  const variant = rowConfig?.variant?.(row) ?? "default"
                  const rowColor = rowConfig?.color?.(row) ?? "default"
                  const textColor = rowConfig?.textColor?.(row) ?? "default"
                  const innerRef = rowConfig?.innerRef?.(row)
                  const onRowClick = rowConfig?.onClick

                  // Derive navigation from getViewUrl
                  const viewUrl = rowConfig?.getViewUrl?.(row)
                  const isNavigable = !onRowClick && !!viewUrl

                  const handleRowClick = onRowClick
                    ? () => onRowClick(row)
                    : isNavigable
                    ? (e) => {
                        if (e.target.closest(INTERACTIVE_SELECTOR)) {
                          return
                        }
                        rowConfig.onViewClick?.(row, "row")
                        if (e.metaKey || e.ctrlKey) {
                          window.open(viewUrl, "_blank")
                        } else {
                          window.location.href = viewUrl
                        }
                      }
                    : undefined

                  const hasSubRow = subRowContent != null

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        ref={innerRef}
                        className={classNames(
                          "group h-16",
                          // When a sub-row follows, drop the data row's
                          // separator so row + sub-row read as one unit.
                          hasSubRow ? "" : "border-b last:border-none",
                          TABLE_ROW_VARIANTS[variant],
                          showRowHighlight
                            ? ROW_HOVER_COLORS[rowColor]
                            : ROW_COLORS[rowColor],
                          TEXT_COLORS[textColor],
                          { "cursor-pointer": handleRowClick }
                        )}
                        onMouseMove={(e) => {
                          if (hoveredRowId !== row.id) setHoveredRowId(row.id)
                          const interactiveEl =
                            e.target.closest(INTERACTIVE_SELECTOR)
                          const interactive =
                            !!interactiveEl && !e.target.closest(".sticky")
                          if (interactive !== isHoveringInteractive)
                            setIsHoveringInteractive(interactive)
                          const hoveringActions =
                            !!e.target.closest(".sticky.right-0")
                          if (hoveringActions !== isHoveringActions)
                            setIsHoveringActions(hoveringActions)
                        }}
                        onMouseLeave={() => {
                          if (menuOpenRowId !== row.id) {
                            setHoveredRowId(null)
                            setIsHoveringInteractive(false)
                            setIsHoveringActions(false)
                          }
                        }}
                        onFocus={() => setFocusedRowId(row.id)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setFocusedRowId(null)
                          }
                        }}
                        onClick={handleRowClick}
                      >
                        {hasLeftColumn && (
                          <td
                            className={classNames(
                              "group/indicator sticky left-0 z-10 cursor-default print:hidden",
                              showRowHighlight
                                ? ROW_HOVER_COLORS[rowColor]
                                : ROW_COLORS[rowColor]
                            )}
                            style={{
                              width: STICKY_COLUMN_WIDTH,
                              minWidth: STICKY_COLUMN_WIDTH,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex h-full items-center justify-center">
                              {viewUrl && !isSelected(row.id) && (
                                <a
                                  href={viewUrl}
                                  aria-label="View"
                                  tabIndex={-1}
                                  onClick={() =>
                                    rowConfig.onViewClick?.(row, "indicator")
                                  }
                                  className={classNames(
                                    "items-center justify-center rounded-full p-1 text-lg text-blue-700 no-underline transition-[transform,background-color] duration-150 ease-out hover:scale-110 hover:bg-blue-50",
                                    showRowHighlight ? "inline-flex" : "hidden",
                                    hasBulkActions &&
                                      "group-hover/indicator:hidden"
                                  )}
                                >
                                  <i className="icon icon-arrow-outward" />
                                </a>
                              )}
                              {hasBulkActions && (
                                <input
                                  type="checkbox"
                                  checked={isSelected(row.id)}
                                  onChange={() => toggleRow(row.id)}
                                  disabled={!rowSelectable}
                                  className={classNames(
                                    {
                                      "cursor-not-allowed opacity-50":
                                        !rowSelectable,
                                    },
                                    viewUrl &&
                                      !isSelected(row.id) &&
                                      showRowHighlight &&
                                      "hidden group-hover/indicator:!block"
                                  )}
                                />
                              )}
                            </div>
                          </td>
                        )}
                        {visibleColumns.map((col, colIndex) => (
                          <td
                            key={col.key}
                            className="overflow-hidden px-4 print:text-xs"
                            align={col.cellAlign}
                            style={{ width: col.width || "200px" }}
                          >
                            {col.render ? col.render(row) : row[col.key]}
                          </td>
                        ))}
                        <td
                          className="print:hidden"
                          style={{ width: "auto" }}
                        />
                        {hasActionsColumn && (
                          <td
                            className={classNames(
                              "sticky right-0 z-10 print:hidden",
                              showRowHighlight
                                ? ROW_HOVER_COLORS[rowColor]
                                : "bg-transparent"
                            )}
                            style={{
                              width: actionsColumnWidth,
                              minWidth: actionsColumnWidth,
                              maxWidth: actionsColumnWidth,
                            }}
                          >
                            {hasSingleRowActions && (
                              <div className="relative h-full">
                                <div className="absolute inset-y-0 right-2 flex items-center">
                                  <RowActions
                                    actions={effectiveActions}
                                    row={row}
                                    isVisible={showActions}
                                    onRequestConfirm={handleRequestConfirm}
                                    onClearHover={clearHover}
                                    onMenuOpenChange={(open) =>
                                      setMenuOpenRowId(open ? row.id : null)
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                      {hasSubRow && (
                        <tr
                          data-sub-row=""
                          className={classNames(
                            "border-b last:border-none",
                            showRowHighlight
                              ? ROW_HOVER_COLORS[rowColor]
                              : ROW_COLORS[rowColor]
                          )}
                          onMouseEnter={() => {
                            setHoveredRowId(row.id)
                            setIsHoveringInteractive(false)
                            setIsHoveringActions(false)
                          }}
                          onMouseLeave={() => {
                            if (menuOpenRowId !== row.id) {
                              setHoveredRowId(null)
                              setIsHoveringInteractive(false)
                              setIsHoveringActions(false)
                            }
                          }}
                        >
                          <td
                            colSpan={dataRowCellCount}
                            className="px-4 pb-3 pt-0"
                          >
                            {subRowContent}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
            </tbody>
            {footerRow && !isLoading && data?.length > 0 && (
              <tfoot className="sticky bottom-0 z-[120] print:static">
                <tr className="h-11 border-t bg-gray-200 font-semibold">
                  {hasLeftColumn && <td />}
                  {visibleColumns.map((col, index) => (
                    <td
                      key={col.key}
                      className="px-4 py-2.5"
                      align={
                        footerRow[col.key] != null ? col.cellAlign : undefined
                      }
                      style={{ width: col.width || "200px" }}
                    >
                      {footerRow[col.key]}
                    </td>
                  ))}
                  <td className="print:hidden" style={{ width: "auto" }} />
                  {hasActionsColumn && <td className="print:hidden" />}
                </tr>
              </tfoot>
            )}
          </table>
        </ScrollShadow>
        <ActionConfirmModal
          isOpen={pendingAction !== null}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmAction}
          title={pendingAction?.confirm?.title ?? ""}
          message={confirmMessage}
          confirmLabel={pendingAction?.confirm?.confirmLabel ?? "Confirm"}
          danger={pendingAction?.danger ?? false}
        />
        {pagination && numberOfPages > 1 && (
          <div className="flex-shrink-0 border-t print:hidden">
            <Pagination
              numberOfPages={numberOfPages}
              page={page}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
      {printRows && (
        <PrintTable
          data={printRows}
          columns={managedColumns}
          idKey={idKey}
          renderSubRow={renderPrintSubRow}
        />
      )}
    </div>
  )
}

DataTableV2.propTypes = {
  name: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(ColumnPropType).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  idKey: PropTypes.string,
  actions: PropTypes.arrayOf(ActionPropType),
  rowConfig: RowConfigPropType,
  selection: SelectionPropType,
  onSelectionChange: PropTypes.func,
  totalCount: PropTypes.number,
  allSelectableIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  pagination: PropTypes.bool,
  page: PropTypes.number,
  numberOfPages: PropTypes.number,
  onPageChange: PropTypes.func,
  sortConfig: SortConfigPropType,
  isLoading: PropTypes.bool,
  maxHeight: PropTypes.string,
  hideSettings: PropTypes.bool,
  hideActions: PropTypes.bool,
  infinite: PropTypes.bool,
  rowCount: PropTypes.number,
  isRowSelectable: PropTypes.func,
  emptyState: PropTypes.node,
  onPrint: PropTypes.func,
  renderPrintSubRow: PropTypes.func,
  onScroll: PropTypes.func,
  searchConfig: SearchConfigPropType,
  footerRow: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node])
  ),
}

export default DataTableV2

// Named exports for utilities, types, and components
export { useSelection } from "./useSelection"
export { default as CellSelect } from "./CellSelect"
export * from "./types"
